<template>
  <div class="page page--fill">
    <data-list
      ref="list"
      title="Comptes"
      :resource="resource"
      :columns="columns"
      :filters="filters"
      :row-to="item => ({ name: 'accountVue', params: { id: item.pk } })">
      <template #summary>
        <kpi-row :items="kpis" />
      </template>
      <template #actions>
        <v-btn
          color="primary"
          depressed
          :to="{ name: 'newAccount' }">
          <v-icon
            left
            small>
            mdi-plus
          </v-icon>
          Nouveau compte
        </v-btn>
      </template>
      <template #name="{ item }">
        <div class="cell-strong">
          {{ item.name }}
        </div>
        <div class="cell-muted">
          {{ item.email }}
        </div>
        <span
          v-if="item.restricted_account"
          class="pill pill--warning mt-1">Désactivé</span>
        <span
          v-if="item.rescind_account"
          class="pill pill--danger mt-1">Résilié</span>
      </template>
      <template #account_type="{ item }">
        <span :class="pillClass(ACCOUNT_TYPE, item.account_type)">{{ labelOf(ACCOUNT_TYPE, item.account_type) }}</span>
      </template>
      <template #modules="{ item }">
        <span class="modules">
          <v-icon
            v-for="module in modules"
            :key="module.key"
            :title="`${module.label} : ${item[module.key] ? 'activé' : 'désactivé'}`"
            :class="{ 'is-off': !item[module.key] }"
            size="18">
            {{ module.icon }}
          </v-icon>
        </span>
      </template>
      <template #row_actions="{ item }">
        <div
          class="row-actions"
          @click.stop>
          <v-btn
            icon
            small
            title="Modifier"
            @click="editedAccount = item">
            <v-icon small>
              mdi-pencil-outline
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :title="item.restricted_account ? 'Réactiver le compte' : 'Désactiver le compte'"
            @click="toggleFlag(item, 'restricted_account')">
            <v-icon small>
              {{ item.restricted_account ? 'mdi-lock-open-variant-outline' : 'mdi-lock-outline' }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :title="item.rescind_account ? 'Annuler la résiliation' : 'Résilier le compte'"
            @click="toggleFlag(item, 'rescind_account')">
            <v-icon small>
              {{ item.rescind_account ? 'mdi-file-restore-outline' : 'mdi-file-cancel-outline' }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            title="Supprimer"
            @click="deleteAccount(item)">
            <v-icon
              small
              color="error">
              mdi-trash-can-outline
            </v-icon>
          </v-btn>
        </div>
      </template>
    </data-list>

    <account-edit-dialog
      :account="editedAccount"
      @close="editedAccount = null"
      @saved="$refs.list.reload()" />
  </div>
</template>

<script>
  import RESOURCES from '@batimentb/vadimm-js-api/resources'
  import DataList from '../components/DataList'
  import KpiRow from '../components/KpiRow'
  import AccountEditDialog from '../components/AccountEditDialog'
  import { ACCOUNT_TYPE, toOptions, pillClass, labelOf, formatDate } from '../labels'

  export default {
    name: 'AccountList',
    components: { DataList, KpiRow, AccountEditDialog },
    data () {
      return {
        kpis: [
          { label: 'Comptes', resource: RESOURCES.ACCOUNT, params: {} },
          { label: 'Promoteurs', resource: RESOURCES.ACCOUNT, params: { account_type: 'promoteur' } },
          { label: 'Commercialisateurs', resource: RESOURCES.ACCOUNT, params: { account_type: 'commercial' } }
        ],
        ACCOUNT_TYPE,
        resource: RESOURCES.ACCOUNT,
        editedAccount: null,
        modules: [
          { key: 'electronic_signature_enabled', label: 'Signature électronique', icon: 'mdi-signature-freehand' },
          { key: 'mgc_enabled', label: 'MGC', icon: 'mdi-share-variant' },
          { key: 'buyer_space_enabled', label: 'Espace acquéreur', icon: 'mdi-account-key-outline' },
          { key: 'brikx_enabled', label: 'Site Brikx', icon: 'mdi-web' },
          { key: 'eidas_enabled', label: 'eIDAS', icon: 'mdi-shield-check-outline' }
        ],
        columns: [
          { key: 'name', label: 'Compte' },
          { key: 'account_type', label: 'Type', width: 150 },
          { key: 'plan.name', label: 'Abonnement' },
          { key: 'phone', label: 'Téléphone', width: 140 },
          { key: 'modules', label: 'Modules', width: 150 },
          { key: 'last_login_at', label: 'Dernière connexion', width: 150, format: formatDate },
          { key: 'created_on', label: 'Créé le', width: 110, format: formatDate },
          { key: 'row_actions', label: '', width: 150, align: 'end' }
        ],
        filters: [
          { key: 'searchable_name', type: 'search', label: 'Rechercher un compte' },
          { key: 'company_email', type: 'search', label: 'Email', width: 220 },
          { key: 'account_type', type: 'select', label: 'Type', items: toOptions(ACCOUNT_TYPE) }
        ]
      }
    },
    methods: {
      pillClass,
      labelOf,
      async toggleFlag (account, field) {
        const texts = {
          restricted_account: account.restricted_account
            ? { title: 'Réactiver le compte ?', message: `Le compte « ${account.name} » retrouve un accès complet.`, ok: 'Réactiver', done: 'Compte réactivé' }
            : { title: 'Désactiver le compte ?', message: `L'accès du compte « ${account.name} » sera restreint.`, ok: 'Désactiver', done: 'Compte désactivé' },
          rescind_account: account.rescind_account
            ? { title: 'Annuler la résiliation ?', message: `Le compte « ${account.name} » n'est plus résilié.`, ok: 'Annuler la résiliation', done: 'Résiliation annulée' }
            : { title: 'Résilier le compte ?', message: `Le compte « ${account.name} » sera marqué comme résilié.`, ok: 'Résilier', done: 'Compte résilié' }
        }[field]
        const ok = await this.$confirm({
          title: texts.title,
          message: texts.message,
          confirmLabel: texts.ok,
          danger: !account[field]
        })
        if (!ok) return
        try {
          await this.$api.patch(RESOURCES.ACCOUNT, account.pk, { [field]: !account[field] })
          this.$awn.success(texts.done)
          this.$refs.list.reload()
        } catch (e) {
          if (e.message !== 'Action annulée') this.$awn.alert('Impossible de modifier ce compte')
        }
      },
      async deleteAccount (account) {
        const ok = await this.$confirm({
          title: 'Supprimer le compte ?',
          message: `Le compte « ${account.name} » sera supprimé.`,
          confirmLabel: 'Supprimer',
          danger: true
        })
        if (!ok) return
        try {
          await this.$api.delete(RESOURCES.ACCOUNT, account.pk)
          this.$awn.success('Compte supprimé')
          this.$refs.list.reload()
        } catch (e) {
          if (e.message !== 'Action annulée') this.$awn.alert('Impossible de supprimer ce compte')
        }
      }
    }
  }
</script>

<style scoped>
  .modules {
    display: inline-flex;
    gap: 6px;
  }
  .modules .v-icon {
    color: #111113;
  }
  .modules .v-icon.is-off {
    color: #cbd5e1;
  }
  .row-actions {
    display: flex;
    justify-content: flex-end;
  }
</style>
